import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import GlassSurface from './GlassSurface';

export type PillNavItem = {
  label: string;
  href: string;
  ariaLabel?: string;
};

export interface PillNavProps {
  logo: string;
  logoAlt?: string;
  brandName?: string;
  items: PillNavItem[];
  activeHref?: string;
  className?: string;
  ease?: string;
  baseColor?: string;
  pillColor?: string;
  hoveredPillTextColor?: string;
  pillTextColor?: string;
  onMobileMenuClick?: () => void;
  initialLoadAnimation?: boolean;
}

const PillNav: React.FC<PillNavProps> = ({
  logo,
  logoAlt = 'Logo',
  brandName,
  items,
  activeHref,
  className = '',
  ease = 'power3.easeOut',
  baseColor = 'rgba(255, 255, 255, 0.12)',
  pillColor = 'rgba(255, 255, 255, 0.18)',
  hoveredPillTextColor = '#060010',
  pillTextColor,
  onMobileMenuClick,
  initialLoadAnimation = true
}) => {
  const resolvedPillTextColor = pillTextColor ?? baseColor;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const circleRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const tlRefs = useRef<Array<gsap.core.Timeline | null>>([]);
  const activeTweenRefs = useRef<Array<gsap.core.Tween | null>>([]);
  const logoImgRef = useRef<HTMLImageElement | null>(null);
  const logoTweenRef = useRef<gsap.core.Tween | null>(null);
  const hamburgerRef = useRef<HTMLButtonElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const navItemsRef = useRef<HTMLDivElement | null>(null);
  const logoRef = useRef<HTMLAnchorElement | HTMLElement | null>(null);

  useEffect(() => {
    const layout = () => {
      circleRefs.current.forEach(circle => {
        if (!circle?.parentElement) return;

        const pill = circle.parentElement as HTMLElement;
        const rect = pill.getBoundingClientRect();
        const { width: w, height: h } = rect;
        const R = ((w * w) / 4 + h * h) / (2 * h);
        const D = Math.ceil(2 * R) + 2;
        const delta = Math.ceil(R - Math.sqrt(Math.max(0, R * R - (w * w) / 4))) + 1;
        const originY = D - delta;

        circle.style.width = `${D}px`;
        circle.style.height = `${D}px`;
        circle.style.bottom = `-${delta}px`;

        gsap.set(circle, {
          xPercent: -50,
          scale: 0,
          transformOrigin: `50% ${originY}px`
        });

        const label = pill.querySelector<HTMLElement>('.pill-label');
        const white = pill.querySelector<HTMLElement>('.pill-label-hover');

        if (label) gsap.set(label, { y: 0 });
        if (white) gsap.set(white, { y: h + 12, opacity: 0 });

        const index = circleRefs.current.indexOf(circle);
        if (index === -1) return;

        tlRefs.current[index]?.kill();
        const tl = gsap.timeline({ paused: true });

        tl.to(circle, { scale: 1.2, xPercent: -50, duration: 2, ease, overwrite: 'auto' }, 0);

        if (label) {
          tl.to(label, { y: -(h + 8), duration: 2, ease, overwrite: 'auto' }, 0);
        }

        if (white) {
          gsap.set(white, { y: Math.ceil(h + 100), opacity: 0 });
          tl.to(white, { y: 0, opacity: 1, duration: 2, ease, overwrite: 'auto' }, 0);
        }

        tlRefs.current[index] = tl;
      });
    };

    layout();

    const onResize = () => layout();
    window.addEventListener('resize', onResize);

    if (document.fonts) {
      document.fonts.ready.then(layout).catch(() => {});
    }

    const menu = mobileMenuRef.current;
    if (menu) {
      gsap.set(menu, { visibility: 'hidden', opacity: 0, scaleY: 1, y: 0 });
    }

    if (initialLoadAnimation) {
      const logo = logoRef.current;
      const navItems = navItemsRef.current;

      if (logo) {
        gsap.set(logo, { scale: 0 });
        gsap.to(logo, {
          scale: 1,
          duration: 0.6,
          ease
        });
      }

      if (navItems) {
        gsap.set(navItems, { width: 0, overflow: 'hidden' });
        gsap.to(navItems, {
          width: 'auto',
          duration: 0.6,
          ease
        });
      }
    }

    return () => window.removeEventListener('resize', onResize);
  }, [items, ease, initialLoadAnimation]);


  const handleEnter = (i: number) => {
    const tl = tlRefs.current[i];
    if (!tl) return;
    activeTweenRefs.current[i]?.kill();
    activeTweenRefs.current[i] = tl.tweenTo(tl.duration(), {
      duration: 0.3,
      ease,
      overwrite: 'auto'
    });
  };

  const handleLeave = (i: number) => {
    const tl = tlRefs.current[i];
    if (!tl) return;
    activeTweenRefs.current[i]?.kill();
    activeTweenRefs.current[i] = tl.tweenTo(0, {
      duration: 0.2,
      ease,
      overwrite: 'auto'
    });
  };

  const handleLogoEnter = () => {
    const img = logoImgRef.current;
    if (!img) return;
    logoTweenRef.current?.kill();
    gsap.set(img, { rotate: 0 });
    logoTweenRef.current = gsap.to(img, {
      rotate: 360,
      duration: 0.2,
      ease,
      overwrite: 'auto'
    });
  };

  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);

    const hamburger = hamburgerRef.current;

    if (hamburger) {
      const lines = hamburger.querySelectorAll('.hamburger-line');
      if (newState) {
        gsap.to(lines[0], { rotation: 45, y: 3, duration: 0.3, ease });
        gsap.to(lines[1], { rotation: -45, y: -3, duration: 0.3, ease });
      } else {
        gsap.to(lines[0], { rotation: 0, y: 0, duration: 0.3, ease });
        gsap.to(lines[1], { rotation: 0, y: 0, duration: 0.3, ease });
      }
    }

    // Mobile menu animation is handled by CSS classes, no GSAP needed

    onMobileMenuClick?.();
  };

  const isExternalLink = (href: string) =>
    href.startsWith('http://') ||
    href.startsWith('https://') ||
    href.startsWith('//') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('#');

  const isRouterLink = (href?: string) => href && !isExternalLink(href);

  const cssVars = {
    '--base': baseColor,
    '--pill-bg': pillColor,
    '--hover-text': hoveredPillTextColor,
    '--pill-text': resolvedPillTextColor,
    '--nav-h': '72px',
    '--logo': '48px',
    '--pill-pad-x': '16px',
    '--pill-gap': '12px'
  } as React.CSSProperties;

  return (
    <nav
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-[1000] w-[90%] md:w-[72%] lg:w-[70%] ${className}`}
      aria-label="Primary Navigation"
      style={{
        maxWidth: '1200px',
        minWidth: '280px',
        ...cssVars
      }}
    >
      {/* Floating Pill Container */}
      <GlassSurface
        width="100%"
        height={undefined}
        borderRadius={9999}
        backgroundOpacity={0.15}
        blur={20}
        displace={2}
        saturation={1.2}
        className="flex items-center overflow-hidden"
        style={{ 
          display: 'flex', 
          alignItems: 'center',
          minHeight: '72px',
          height: '72px',
          transition: 'all 0.3s ease-out'
        }}
      >
        {/* Top Row - Logo, Brand, Links, and Hamburger */}
        <div 
          className="flex items-center justify-between px-6 md:px-8 md:px-10 w-full flex-shrink-0"
          style={{ minHeight: '72px' }}
        >
          {/* Left Section - Brand (Larger) */}
          <div 
            className="flex items-center gap-3 md:gap-4 flex-shrink-0"
            ref={el => {
              logoRef.current = el;
            }}
          >
            {isRouterLink(items?.[0]?.href) ? (
              <Link
                to={items[0].href}
                aria-label="Home"
                onMouseEnter={handleLogoEnter}
                className="inline-flex items-center justify-center"
              >
                <img 
                  src={logo} 
                  alt={logoAlt} 
                  ref={logoImgRef} 
                  className="w-10 h-10 md:w-12 md:h-12 object-contain transition-transform duration-300 hover:scale-110"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </Link>
            ) : (
              <a
                href={items?.[0]?.href || '#'}
                aria-label="Home"
                onMouseEnter={handleLogoEnter}
                className="inline-flex items-center justify-center"
              >
                <img 
                  src={logo} 
                  alt={logoAlt} 
                  ref={logoImgRef} 
                  className="w-10 h-10 md:w-12 md:h-12 object-contain transition-transform duration-300 hover:scale-110"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </a>
            )}
            {brandName && (
              <span
                className="text-white font-bold whitespace-nowrap"
                style={{
                  fontSize: '20px',
                  lineHeight: '1',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                }}
              >
                {brandName}
              </span>
            )}
          </div>

          {/* Right Section - Navigation Links (Desktop Only) */}
          <div
            ref={navItemsRef}
            className="relative items-center hidden md:flex flex-shrink-0 ml-auto"
          >
            <ul
              role="menubar"
              className="list-none flex items-center m-0"
              style={{ gap: '12px' }}
            >
              {items.slice(1).map((item, i) => {
                const actualIndex = i + 1; // Adjust index for circleRefs
                const isActive = activeHref === item.href;

                const pillStyle: React.CSSProperties = {
                  background: 'var(--pill-bg)',
                  color: 'var(--pill-text)',
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  height: 'auto',
                  minHeight: '40px'
                };

                const PillContent = (
                  <>
                    <span
                      className="hover-circle absolute left-1/2 bottom-0 rounded-full z-[1] block pointer-events-none"
                      style={{
                        background: 'var(--base, #000)',
                        willChange: 'transform'
                      }}
                      aria-hidden="true"
                      ref={el => {
                        circleRefs.current[actualIndex] = el;
                      }}
                    />
                    <span className="label-stack relative inline-block leading-[1] z-[2]">
                      <span
                        className="pill-label relative z-[2] inline-block leading-[1]"
                        style={{ willChange: 'transform' }}
                      >
                        {item.label}
                      </span>
                      <span
                        className="pill-label-hover absolute left-0 top-0 z-[3] inline-block"
                        style={{
                          color: 'var(--hover-text, #fff)',
                          willChange: 'transform, opacity'
                        }}
                        aria-hidden="true"
                      >
                        {item.label}
                      </span>
                    </span>
                    {isActive && (
                      <span
                        className="absolute left-1/2 -bottom-[6px] -translate-x-1/2 w-3 h-3 rounded-full z-[4]"
                        style={{ background: 'var(--base, #000)' }}
                        aria-hidden="true"
                      />
                    )}
                  </>
                );

                const basePillClasses =
                  'relative overflow-hidden inline-flex items-center justify-center no-underline rounded-full box-border font-semibold text-[16px] leading-[1] tracking-[0.2px] whitespace-nowrap cursor-pointer px-0';

                return (
                  <li key={item.href} role="none" className="flex items-center">
                    {isRouterLink(item.href) ? (
                      <Link
                        role="menuitem"
                        to={item.href}
                        className={basePillClasses}
                        style={pillStyle}
                        aria-label={item.ariaLabel || item.label}
                        onMouseEnter={() => handleEnter(actualIndex)}
                        onMouseLeave={() => handleLeave(actualIndex)}
                      >
                        {PillContent}
                      </Link>
                    ) : (
                      <a
                        role="menuitem"
                        href={item.href}
                        className={basePillClasses}
                        style={pillStyle}
                        aria-label={item.ariaLabel || item.label}
                        onMouseEnter={() => handleEnter(actualIndex)}
                        onMouseLeave={() => handleLeave(actualIndex)}
                      >
                        {PillContent}
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Mobile Menu Button */}
          <button
            ref={hamburgerRef}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
            className="md:hidden rounded-full border-0 flex flex-col items-center justify-center cursor-pointer relative flex-shrink-0"
            style={{
              width: '56px',
              height: '56px',
              background: 'transparent',
              padding: '18px 16px',
              gap: '7px'
            }}
          >
            <span
              className={`hamburger-line w-7 h-0.5 rounded origin-center transition-all duration-300 ease-out ${
                isMobileMenuOpen ? 'rotate-45 translate-y-3' : ''
              }`}
              style={{ background: 'rgba(255, 255, 255, 0.9)' }}
            />
            <span
              className={`hamburger-line w-7 h-0.5 rounded origin-center transition-all duration-300 ease-out ${
                isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
              }`}
              style={{ background: 'rgba(255, 255, 255, 0.9)' }}
            />
            <span
              className={`hamburger-line w-7 h-0.5 rounded origin-center transition-all duration-300 ease-out ${
                isMobileMenuOpen ? '-rotate-45 -translate-y-3' : ''
              }`}
              style={{ background: 'rgba(255, 255, 255, 0.9)' }}
            />
          </button>
        </div>
      </GlassSurface>

      {/* Mobile Menu - Full screen overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-[998]"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            top: '88px' // Start below the navbar (72px height + 16px top margin)
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      <div
        ref={mobileMenuRef}
        className="md:hidden fixed left-4 right-4 z-[999]"
        style={{
          top: 'calc(72px + 16px + 16px)', // Navbar height (72px) + top margin (16px) + extra margin (16px)
          transform: isMobileMenuOpen ? 'translateY(0) scale(1)' : 'translateY(-20px) scale(0.95)',
          opacity: isMobileMenuOpen ? 1 : 0,
          visibility: isMobileMenuOpen ? 'visible' : 'hidden',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out, visibility 0.3s',
          pointerEvents: isMobileMenuOpen ? 'auto' : 'none',
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto'
        }}
      >
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            borderRadius: '27px',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
            padding: '24px'
          }}
        >
          <ul className="list-none m-0 flex flex-col gap-3">
            {items.slice(1).map(item => {
              const defaultStyle: React.CSSProperties = {
                background: 'rgba(255, 255, 255, 0.15)',
                color: 'rgba(255, 255, 255, 0.95)',
                display: 'block',
                padding: '12px 20px',
                borderRadius: '9999px',
                fontSize: '16px',
                fontWeight: 600,
                textDecoration: 'none',
                transition: 'all 0.2s ease'
              };
              const hoverIn = (e: React.MouseEvent<HTMLAnchorElement>) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 1)';
              };
              const hoverOut = (e: React.MouseEvent<HTMLAnchorElement>) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.95)';
              };

              return (
                <li key={item.href}>
                  {isRouterLink(item.href) ? (
                    <Link
                      to={item.href}
                      style={defaultStyle}
                      onMouseEnter={hoverIn}
                      onMouseLeave={hoverOut}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <a
                      href={item.href}
                      style={defaultStyle}
                      onMouseEnter={hoverIn}
                      onMouseLeave={hoverOut}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default PillNav;

